import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Anchor, Button } from '../../lib/components'
import { kycServices } from '../../services'
import { RootState } from '../../store'
import { showBanner } from '../../global-state/banner-slice'
import {
  loadSumsubWebSdkBuilder,
  shouldCloseSumsubOverlayOnMessage,
  shouldRefreshKycOnSumsubMessage,
  SUMSUB_OVERLAY_CLOSE_DELAY_MS,
  SumsubWebSdkInstance,
} from '../../lib/utils/sumsub-websdk'
import './kyc.scss'

interface SumSubProps {
  kycStatus: string | undefined
  noOfRejectedKyc: number
  getKYC: () => Promise<void>
  clientAgreementSigned: boolean
}

const SUMSUB_CONTAINER_ID = 'sumsub-websdk-container'

const SumSub = ({ kycStatus, noOfRejectedKyc, getKYC, clientAgreementSigned }: SumSubProps) => {
  const MAX_RETRY = 3
  const POLL_STATUS_INTERVAL = 3000 //ms
  const { authToken, accountStatus } = useSelector((state: RootState) => state.investor)
  const [KYCLinkText, setKYCLinkText] = useState('Start')
  const [isOverlayOpen, setIsOverlayOpen] = useState(false)
  const [isClosingSoon, setIsClosingSoon] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const sdkInstanceRef = useRef<SumsubWebSdkInstance | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingAccessTokenRef = useRef<string | null>(null)

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const destroySdk = () => {
    try {
      sdkInstanceRef.current?.destroy()
    } catch (error) {
      console.error('Error destroying Sumsub WebSDK:', error)
    }
    sdkInstanceRef.current = null
  }

  const closeOverlay = async () => {
    clearCloseTimer()
    destroySdk()
    pendingAccessTokenRef.current = null
    setIsClosingSoon(false)
    setIsOverlayOpen(false)
    document.body.style.overflow = ''
    await getKYC()
  }

  const scheduleOverlayClose = () => {
    if (closeTimerRef.current) return
    setIsClosingSoon(true)
    closeTimerRef.current = setTimeout(() => {
      void closeOverlay()
    }, SUMSUB_OVERLAY_CLOSE_DELAY_MS)
  }

  const fetchAccessToken = async (): Promise<string | null> => {
    try {
      const response = await kycServices.generateSumsubAccessToken(
        {
          Authorization: authToken ? authToken : '',
        },
        'INDIVIDUAL',
      )
      const token = response?.data?.token
      return token || null
    } catch (error: any) {
      console.error('Error generating KYC access token:', error)

      if (error?.response?.data?.code === 'self-declaration-not-signed') {
        dispatch(
          showBanner({
            text: 'Please complete the Client Agreement & Self-Declaration before starting KYC.',
            variant: 'error-banner',
          }),
        )
      } else {
        dispatch(
          showBanner({
            text: 'There was an error starting the KYC process. Please try again later.',
            variant: 'error-banner',
          }),
        )
      }
      return null
    }
  }

  const launchWebSdk = async (accessToken: string) => {
    const snsWebSdk = await loadSumsubWebSdkBuilder()
    const instance = snsWebSdk
      .init(accessToken, async () => {
        const refreshed = await fetchAccessToken()
        if (!refreshed) {
          throw new Error('Unable to refresh Sumsub access token')
        }
        return refreshed
      })
      .withConf({ lang: 'en' })
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .onMessage((type) => {
        if (shouldRefreshKycOnSumsubMessage(type)) {
          void getKYC()
        }
        if (shouldCloseSumsubOverlayOnMessage(type)) {
          scheduleOverlayClose()
        }
      })
      .build()

    sdkInstanceRef.current = instance
    instance.launch(`#${SUMSUB_CONTAINER_ID}`)
  }

  useEffect(() => {
    if (!isOverlayOpen) return
    const accessToken = pendingAccessTokenRef.current
    if (!accessToken) return
    pendingAccessTokenRef.current = null

    const run = async () => {
      try {
        await launchWebSdk(accessToken)
      } catch (error) {
        console.error('Error launching Sumsub WebSDK:', error)
        destroySdk()
        setIsOverlayOpen(false)
        dispatch(
          showBanner({
            text: 'There was an error starting the KYC process. Please try again later.',
            variant: 'error-banner',
          }),
        )
      }
    }

    void run()
  }, [isOverlayOpen])

  const handleButtonClick = async () => {
    if (!clientAgreementSigned) {
      dispatch(
        showBanner({
          text: 'Please complete the Client Agreement & Self-Declaration before starting KYC.',
          variant: 'warning-banner',
        }),
      )
      return
    }

    if (kycStatus === 'REJECTED') {
      const data = {
        acceptedTNC: true,
        acceptedNDA: true,
      }

      const response = await kycServices.acceptTNCAndNDA_v1(data, {
        Authorization: authToken ? authToken : '',
      })

      if (response && response.status === 200) {
        await getKYC()
      }
    }

    const accessToken = await fetchAccessToken()
    if (!accessToken) return

    clearCloseTimer()
    destroySdk()
    setIsClosingSoon(false)
    pendingAccessTokenRef.current = accessToken
    document.body.style.overflow = 'hidden'
    setIsOverlayOpen(true)
  }

  useEffect(() => {
    const intervalID = setInterval(async () => {
      const shouldPollStatus =
        isOverlayOpen ||
        (kycStatus !== 'APPROVED' && kycStatus !== 'REJECTED') ||
        (kycStatus === 'REJECTED' && noOfRejectedKyc < MAX_RETRY)

      if (shouldPollStatus) {
        await getKYC()
      }
    }, POLL_STATUS_INTERVAL)

    return () => clearInterval(intervalID)
  }, [kycStatus, noOfRejectedKyc, isOverlayOpen])

  useEffect(() => {
    if (kycStatus === 'NOT_STARTED') {
      setKYCLinkText('Start')
    } else if (kycStatus === 'INITIATED') {
      setKYCLinkText('Continue')
    } else if (kycStatus === 'RESUBMIT') {
      setKYCLinkText('Continue')
    } else if (kycStatus === 'REJECTED') {
      setKYCLinkText('Start Again')
    } else if (kycStatus === 'EXPIRED') {
      setKYCLinkText('Start Again')
    } else if (kycStatus === 'RESUBMIT_SOF') {
      setKYCLinkText('Continue')
    }
  }, [kycStatus])

  useEffect(() => {
    return () => {
      clearCloseTimer()
      destroySdk()
      document.body.style.overflow = ''
    }
  }, [])

  const showExploreOpportunities = ['PENDING', 'APPROVED'].includes(kycStatus ?? 'NOT_STARTED')
  const shouldHideStartUi =
    showExploreOpportunities || (kycStatus === 'REJECTED' && noOfRejectedKyc >= MAX_RETRY)

  if (shouldHideStartUi && !isOverlayOpen && !showExploreOpportunities) {
    return <></>
  }

  // KYC starts after client agreement is signed
  const isButtonDisabled = accountStatus === 'DORMANT' || !clientAgreementSigned

  return (
    <div>
      {showExploreOpportunities && !isOverlayOpen && accountStatus !== 'DORMANT' && (
        <div className='kyc-action-btn-container'>
          <Button
            classes={['auto-btn-width']}
            variant='primary-btn'
            text='Explore Opportunities'
            handleClick={() => navigate('/opportunities')}
          />
        </div>
      )}
      {!shouldHideStartUi && (
        <>
          <div>
            {accountStatus === 'DORMANT' ? (
              <div
                className='kyc-sub-heading-text-container-note mb-16'
                data-testid='kyc-text-dormant'
              >
                <p className='basic kyc-sub-heading-text-note'>
                  Your account is dormant. Please contact{' '}
                  <Anchor
                    variant='basic-anchor'
                    classes={['kyc-sub-heading-text-anchor']}
                    text={'Credibila Support'}
                    href='/contact-us'
                  />{' '}
                  to unblock your account first. Once your account is active, you can proceed with
                  the KYC process.
                </p>
              </div>
            ) : (
              <>
                {!clientAgreementSigned && (
                  <div
                    className='kyc-sub-heading-text-container-note mb-16'
                    data-testid='kyc-text-client-agreement-required'
                  >
                    <p className='basic kyc-sub-heading-text-note'>
                      Complete the Client Agreement & Self-Declaration first. You can start KYC
                      after signing.
                    </p>
                  </div>
                )}
                {kycStatus === 'EXPIRED' && (
                  <div
                    className='kyc-sub-heading-text-container-note mb-16'
                    data-testid='kyc-text-expired'
                  >
                    <p className='basic kyc-sub-heading-text-note'>
                      Your KYC has expired. Please complete the KYC once again for investing in
                      opportunities. &nbsp;
                    </p>
                  </div>
                )}
                {clientAgreementSigned && kycStatus !== 'REJECTED' && kycStatus !== 'RESUBMIT' && (
                  <div className='kyc-sub-heading-text-container-note mb-16'>
                    <p className='basic kyc-sub-heading-text-note'>
                      During your KYC, you can always leave anytime and return to Home Page. Your
                      progress is saved so you continue from where you left.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          {accountStatus !== 'DORMANT' && (
            <div className='kyc-action-btn-container'>
              <Button
                classes={['auto-btn-width']}
                variant='primary-btn'
                text={KYCLinkText}
                handleClick={handleButtonClick}
                isDisabled={isButtonDisabled}
              />
            </div>
          )}
        </>
      )}
      {isOverlayOpen && (
        <div className='sumsub-websdk-overlay' data-testid='sumsub-websdk-overlay'>
          <div className='sumsub-websdk-overlay__panel'>
            <div className='sumsub-websdk-overlay__header'>
              <Button
                variant='secondary-btn'
                text='Close'
                classes={['auto-btn-width']}
                handleClick={() => {
                  void closeOverlay()
                }}
              />
            </div>
            {isClosingSoon && (
              <p className='basic mt-8 sumsub-websdk-overlay__closing' data-testid='sumsub-closing'>
                Documents submitted. This window will close in a few seconds.
              </p>
            )}
            <div id={SUMSUB_CONTAINER_ID} className='sumsub-websdk-overlay__container' />
          </div>
        </div>
      )}
    </div>
  )
}

export { SumSub }
